import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PropTypes from "prop-types";
import * as React from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  DeleteIcon,
  DragIndicatorIcon,
  PencilIcon,
  RestartIcon,
} from "../../../themeV2";
import useInfluencerServices from "../../../hooks/useInfluencerServices";
import fetcher from "../../../hooks/fetcher";
import { CATEGORIES } from "../../../domain/influencerService";
import { usd } from "../../../util/formatCurrency";
import ServiceFormDialog from "../../Dashboard/ManageServices/ServiceFormDialog";
import DragOverlayService from "../../Dashboard/ManageServices/DragOverlayService";
import { categoryChipClass } from "../../Dashboard/ManageServices/categoryChipClass";
import s from "./ServicesContent.module.css";

const categoryLabels = {
  [CATEGORIES.QUOTE]: "Quote",
  [CATEGORIES.DOODLE]: "Doodle",
  [CATEGORIES.CUSTOM]: "Custom",
};

/* ─── Individual service card ─── */

function ServiceCard({
  id,
  service,
  influencerId,
  refresh,
  setError,
  readOnly,
  onEdit,
  formBusy,
}) {
  const [isLoading, setIsLoading] = React.useState(false);

  const isInactive = !service.isActive;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isInactive || readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const handleDeactivate = async () => {
    setIsLoading(true);
    try {
      await fetcher(
        `/api/influencer-services/${influencerId}/${service.influencerServiceId}`,
        { method: "DELETE" },
      );
      await refresh();
    } catch (err) {
      setError(err?.json?.message ?? "Failed to deactivate service");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async () => {
    setIsLoading(true);
    try {
      await fetcher(
        `/api/influencer-services/${influencerId}/${service.influencerServiceId}`,
        {
          method: "PUT",
          body: JSON.stringify({ active: true }),
        },
      );
      await refresh();
    } catch (err) {
      setError(err?.json?.message ?? "Failed to reactivate service");
    } finally {
      setIsLoading(false);
    }
  };

  const cardClassName = [s.card, isInactive && s.cardInactive]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Card.Root
        ref={setNodeRef}
        style={style}
        variant="outlined"
        className={cardClassName}
        {...attributes}
      >
        <div className={s.cardTop}>
          {!readOnly && !isInactive && (
            <button
              type="button"
              className={s.dragHandle}
              aria-label="Drag to reorder"
              {...listeners}
            >
              <DragIndicatorIcon />
            </button>
          )}
          <Chip
            label={categoryLabels[service.category] ?? service.category}
            size="sm"
            className={categoryChipClass(service.category, s)}
          />
          <Typography variant="body-lg" className={s.cardLabel}>
            {service.label}
          </Typography>
          <Typography variant="label-lg">{usd(service.splitAmount)}</Typography>
        </div>

        <div className={s.cardBottom}>
          {isInactive ? (
            <Badge variant="status" status="unavailable">
              Inactive
            </Badge>
          ) : (
            <Badge variant="status" status="success">
              Active
            </Badge>
          )}
          {service.maxQuantity > 1 && (
            <Typography variant="body-md" color="variant">
              Max qty: {service.maxQuantity}
            </Typography>
          )}
          {service.exclusionGroup && (
            <Chip
              label={service.exclusionGroup}
              size="sm"
              variant="assist"
              aria-label={`Exclusion group: ${service.exclusionGroup}`}
            />
          )}
          <div className={s.cardActionsSpacer} />

          {!readOnly && (
            <div className={s.cardActions}>
              {!isInactive && (
                <Tooltip.Root>
                  <Tooltip.Trigger
                    render={
                      <IconButton
                        size="sm"
                        variant="text"
                        disabled={isLoading || formBusy}
                        onClick={() => onEdit(service)}
                        label="Edit service"
                      >
                        <PencilIcon />
                      </IconButton>
                    }
                  />
                  <Tooltip.Popup>Edit</Tooltip.Popup>
                </Tooltip.Root>
              )}

              {isInactive ? (
                <Tooltip.Root>
                  <Tooltip.Trigger
                    render={
                      <IconButton
                        size="sm"
                        variant="text"
                        disabled={isLoading}
                        onClick={handleReactivate}
                        label="Reactivate service"
                      >
                        <RestartIcon />
                      </IconButton>
                    }
                  />
                  <Tooltip.Popup>Reactivate</Tooltip.Popup>
                </Tooltip.Root>
              ) : (
                <Tooltip.Root>
                  <Tooltip.Trigger
                    render={
                      <IconButton
                        size="sm"
                        variant="text"
                        disabled={isLoading}
                        onClick={handleDeactivate}
                        label="Deactivate service"
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  />
                  <Tooltip.Popup>Deactivate</Tooltip.Popup>
                </Tooltip.Root>
              )}
            </div>
          )}
        </div>
      </Card.Root>
    </>
  );
}

ServiceCard.propTypes = {
  id: PropTypes.number.isRequired,
  service: PropTypes.shape({
    influencerServiceId: PropTypes.number.isRequired,
    category: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    splitAmount: PropTypes.number.isRequired,
    maxWordCount: PropTypes.number,
    requiresTextInput: PropTypes.bool,
    maxQuantity: PropTypes.number,
    exclusionGroup: PropTypes.string,
    isActive: PropTypes.bool.isRequired,
  }).isRequired,
  influencerId: PropTypes.number.isRequired,
  refresh: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  formBusy: PropTypes.bool.isRequired,
};

/* ─── Main content component ─── */

const ServicesContent = React.memo(function ServicesContent({
  influencerId,
  readOnly,
}) {
  const [error, setError] = React.useState("");
  const [activeService, setActiveService] = React.useState(null);
  const [formService, setFormService] = React.useState(undefined);
  const { services, isLoading, isError, refresh } =
    useInfluencerServices(influencerId);

  const isFormOpen = formService !== undefined;
  const handleOpenAdd = React.useCallback(() => setFormService(null), []);
  const handleOpenEdit = React.useCallback((svc) => setFormService(svc), []);
  const handleCloseForm = React.useCallback(
    () => setFormService(undefined),
    [],
  );

  const [localServices, setLocalServices] = React.useState(services);
  React.useEffect(() => {
    setLocalServices(services);
  }, [services]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sortableIds = React.useMemo(
    () => localServices.map((svc) => svc.influencerServiceId),
    [localServices],
  );

  const persistPositions = React.useCallback(
    async (reorderedServices) => {
      try {
        await fetcher(`/api/influencer-services/${influencerId}/positions`, {
          method: "PUT",
          body: JSON.stringify({
            positions: reorderedServices.map((svc, index) => ({
              influencerServiceId: svc.influencerServiceId,
              position: index,
            })),
          }),
        });
        await refresh();
      } catch (err) {
        setError(err?.json?.message ?? "Failed to update positions");
        await refresh();
      }
    },
    [influencerId, refresh],
  );

  const handleDragStart = React.useCallback(
    (event) => {
      const service = localServices.find(
        (svc) => svc.influencerServiceId === event.active.id,
      );
      setActiveService(service ?? null);
    },
    [localServices],
  );

  const handleDragEnd = React.useCallback(
    (event) => {
      setActiveService(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localServices.findIndex(
        (svc) => svc.influencerServiceId === active.id,
      );
      const newIndex = localServices.findIndex(
        (svc) => svc.influencerServiceId === over.id,
      );

      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(localServices, oldIndex, newIndex);
      setLocalServices(reordered);
      persistPositions(reordered);
    },
    [localServices, persistPositions],
  );

  const handleDragCancel = React.useCallback(() => {
    setActiveService(null);
  }, []);

  return (
    <div>
      <Typography variant="body-lg" color="variant" className={s.description}>
        Manage personalization services such as quotes, doodles, or custom
        upcharges. Services can be toggled on or off for each product.
      </Typography>

      {isLoading && <Skeleton variant="rect" width="100%" height={118} />}

      {isError && (
        <Alert severity="error">There was an error loading services.</Alert>
      )}

      {!isLoading && !isError && localServices.length === 0 && (
        <Typography variant="body-lg" color="variant">
          No services defined yet. Add a service to get started.
        </Typography>
      )}

      {!isLoading && localServices.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            <div className={s.list}>
              {localServices.map((service) => (
                <ServiceCard
                  key={service.influencerServiceId}
                  id={service.influencerServiceId}
                  service={service}
                  influencerId={influencerId}
                  refresh={refresh}
                  setError={setError}
                  readOnly={readOnly}
                  onEdit={handleOpenEdit}
                  formBusy={isFormOpen}
                />
              ))}
            </div>
          </SortableContext>
          {!readOnly && (
            <DragOverlay>
              <DragOverlayService service={activeService} />
            </DragOverlay>
          )}
        </DndContext>
      )}

      {Boolean(error) && (
        <Alert severity="error" style={{ marginBlockStart: "var(--space-2)" }}>
          {error}
        </Alert>
      )}

      {!readOnly && !isLoading && (
        <div className={s.footer}>
          <Button
            variant="filled"
            onClick={handleOpenAdd}
            disabled={isFormOpen}
            aria-label="Add service"
          >
            Add Service
          </Button>
        </div>
      )}

      {isFormOpen && (
        <ServiceFormDialog
          influencerId={influencerId}
          refresh={refresh}
          service={formService}
          open
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
});

ServicesContent.displayName = "ServicesContent";

ServicesContent.propTypes = {
  influencerId: PropTypes.number.isRequired,
  readOnly: PropTypes.bool.isRequired,
};

export default ServicesContent;
